import { useState, useMemo, useEffect } from "react";
import { Calendar as CalIcon, Clock, Loader2, CheckCircle2, MapPin, Video } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import { slotsForDate, formatDateISO, type Slot } from "@/lib/slots";
import { getTakenSlots, createBookingCheckout } from "@/lib/booking.functions";
import { type PublicProduct } from "@/lib/content.functions";
import { StripeCheckoutPanel } from "@/components/StripeCheckoutPanel";
import { getStripeEnvironment } from "@/lib/stripe";
import { useT } from "@/lib/i18n";

type Step = "date" | "slot" | "details" | "choose" | "pay" | "confirmed";
const STEPS: Step[] = ["date", "slot", "details", "choose", "pay"];

function formatPrice(cents: number, currency: string, lang: string) {
  const locale = lang === "pt" ? "pt-BR" : lang === "en" ? "en-GB" : "es-ES";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function isOnlineProduct(p: PublicProduct) {
  const s = `${p.slug} ${p.name}`.toLowerCase();
  return s.includes("online") || s.includes("video") || s.includes("remoto");
}

export function ConsultationBookingForm({
  product,
  products,
}: {
  product: PublicProduct;
  products?: PublicProduct[];
}) {
  const { tk, lang } = useT();

  const [step, setStep] = useState<Step>("date");
  const [date, setDate] = useState<Date | undefined>();
  const [slot, setSlot] = useState<Slot | null>(null);
  const [details, setDetails] = useState({ fullName: "", email: "", phone: "", reason: "" });
  const [accepted, setAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PublicProduct | null>(null);
  const [existingClient, setExistingClient] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const getTaken = useServerFn(getTakenSlots);
  const createCheckout = useServerFn(createBookingCheckout);
  const queryClient = useQueryClient();

  const isoDate = date ? formatDateISO(date) : "";

  const takenQuery = useQuery({
    queryKey: ["taken-slots", isoDate],
    queryFn: () => getTaken({ data: { date: isoDate } }),
    enabled: Boolean(isoDate),
    // Availability must be fresh on every date selection — no caching between days.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });

  const consultationProducts = (products ?? [])
    .filter((p) => p.form_key === product.form_key && p.active === true)
    .sort((a, b) => a.sort_order - b.sort_order);
  // Fallback to the product passed in via the route if backoffice only has one.
  const productOptions = consultationProducts.length > 0 ? consultationProducts : [product];

  const avail = takenQuery.data;
  const taken = avail?.taken ?? [];
  const displaySlots = useMemo<Slot[]>(() => {
    if (!date || avail?.fullDayBlocked) return [];
    // New API: render the server-provided slots exactly as they arrive
    // (already filtered for taken/blocked/schedule, may be non-contiguous).
    if (avail?.hasSchedule) return avail.available ?? [];
    // Legacy: local grid, hiding any slot overlapping a blocked range.
    const blocked = avail?.blocked ?? [];
    return slotsForDate(date).filter(
      (s) => !blocked.some((b) => s.start < b.end && s.end > b.start),
    );
  }, [date, avail]);

  const dateLocale = lang === "pt" ? "pt-BR" : lang === "en" ? "en-GB" : "es-ES";

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!date || !slot) throw new Error(tk("contacto.a1d0a3", "Faltan datos"));
      if (!existingClient && !selectedProduct) {
        throw new Error(tk("contacto.choose.missing", "Elige una modalidad"));
      }
      const productForBooking = existingClient ? (selectedProduct ?? productOptions[0]) : selectedProduct!;
      const returnUrl = `${window.location.origin}/reserva-confirmada?session_id={CHECKOUT_SESSION_ID}`;
      return createCheckout({
        data: {
          productId: productForBooking.id,
          date: formatDateISO(date),
          start: slot.start,
          end: slot.end,
          ...details,
          returnUrl,
          environment: getStripeEnvironment(),
          existingClient,
          marketingConsent,
        },
      });
    },
    onSuccess: (r) => {
      if (!r.ok) {
        if (r.code === "conflict") {
          toast.error(
            tk("contacto.err.conflict", "Este horario ya no está disponible. Por favor, elige otra hora."),
          );
          setSlot(null);
          setStep("slot");
          queryClient.invalidateQueries({ queryKey: ["taken-slots"] });
        } else if (r.code === "gone") {
          toast.error(
            tk("contacto.err.gone", "Este servicio ya no está disponible. Por favor, recarga la página."),
          );
        } else {
          const labels: Record<string, string> = {
            fullName: tk("contacto.c3a7af", "Nombre y apellidos"),
            email: "Email",
            phone: tk("contacto.624704", "Teléfono móvil"),
            reason: tk("contacto.253d92", "Motivo de la consulta"),
          };
          const msgs = Object.entries(r.fieldErrors ?? {}).map(
            ([field, msg]) => `${labels[field] ?? field}: ${msg}`,
          );
          toast.error(
            msgs.length > 0
              ? msgs.join(" · ")
              : tk("contacto.err.validation", "Revisa los datos del formulario e inténtalo de nuevo."),
          );
          setStep("details");
        }
        return;
      }
      // Refresh availability after a successful booking.
      queryClient.invalidateQueries({ queryKey: ["taken-slots"] });
      if (r.pendingConfirmation || !r.clientSecret) {
        setStep("confirmed");
      } else {
        setClientSecret(r.clientSecret);
        setStep("pay");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  useEffect(() => {
    if (step === "pay" || step === "confirmed") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const stepIndex = (s: Step) => STEPS.indexOf(s);

  return (
    <section id="reservar" className="max-w-6xl mx-auto px-6 py-16 scroll-mt-24">
      <div className="flex items-center justify-center gap-2 sm:gap-4 mb-12 text-[10px] tracking-[0.3em] uppercase flex-wrap">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 sm:gap-4">
            <span
              className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center ${
                step === s
                  ? "border-gold bg-gold text-primary-foreground"
                  : stepIndex(step) > i
                    ? "border-gold/60 text-gold"
                    : "border-gold/30 text-gold/60"
              }`}
            >
              {i + 1}
            </span>
            <span className={`hidden sm:inline ${step === s ? "text-gold" : "text-foreground/50"}`}>
              {s === "date"
                ? tk("contacto.bcc476", "Fecha")
                : s === "slot"
                  ? tk("contacto.f07dc2", "Hora")
                  : s === "details"
                    ? tk("contacto.bb0a7c", "Datos")
                    : s === "choose"
                      ? tk("contacto.choose.step", "Modalidad")
                      : tk("contacto.dbb142", "Pago")}
            </span>
            {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-gold/20" />}
          </div>
        ))}
      </div>

      {step === "date" && (
        <div className="rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12 flex flex-col items-center">
          <div className="flex items-center gap-3 text-gold mb-6">
            <CalIcon size={18} strokeWidth={1.3} />
            <span className="text-xs tracking-[0.3em] uppercase">{tk("contacto.b54424", "Elige el día")}</span>
          </div>
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              setDate(d);
              setSlot(null);
              if (d) setStep("slot");
            }}
            disabled={(d) => {
              if (d < new Date(new Date().toDateString())) return true;
              const day = d.getDay();
              return day === 0 || day === 6;
            }}
            className="pointer-events-auto"
          />
          <p className="text-xs text-muted-foreground mt-6 text-center max-w-md">
            {tk("contacto.c8df46", "Atendemos de lunes a jueves de 10:00 a 14:00 y 16:00 a 19:00, y viernes de 10:00 a 14:00.")}
          </p>
        </div>
      )}

      {step === "slot" && date && (
        <div className="rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-3 text-gold">
              <Clock size={18} strokeWidth={1.3} />
              <span className="text-xs tracking-[0.3em] uppercase">
                {date.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long" })}
              </span>
            </div>
            <button
              onClick={() => setStep("date")}
              className="text-[10px] tracking-[0.3em] uppercase text-gold/70 hover:text-gold"
            >
              ← {tk("contacto.0d027e", "Cambiar fecha")}
            </button>
          </div>
          {takenQuery.isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="animate-spin" size={20} />
            </div>
          ) : avail?.fullDayBlocked ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              {tk("contacto.day.blocked", "Este día no está disponible. Por favor, elige otra fecha.")}
            </p>
          ) : displaySlots.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              {tk("contacto.day.full", "No quedan horas disponibles este día. Elige otra fecha.")}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {displaySlots.map((s) => {
                const isTaken = !avail?.hasSchedule && taken.includes(s.start);
                return (
                  <button
                    key={s.start}
                    disabled={isTaken}
                    onClick={() => {
                      setSlot(s);
                      setStep("details");
                    }}
                    className={`rounded-xl border py-3 px-4 text-sm transition-colors ${
                      isTaken
                        ? "border-foreground/10 text-foreground/30 line-through cursor-not-allowed"
                        : "border-gold/30 text-foreground hover:border-gold hover:bg-gold/10"
                    }`}
                  >
                    {s.start}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === "details" && date && slot && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const schema = z.object({
              fullName: z
                .string()
                .trim()
                .min(3, tk("contacto.val.name", "Introduzca nombre y apellidos"))
                .max(120)
                .refine((v) => v.split(/\s+/).filter(Boolean).length >= 2, {
                  message: tk("contacto.val.name", "Introduzca nombre y apellidos"),
                }),
              email: z
                .string()
                .trim()
                .email(tk("contacto.val.email", "Email no válido"))
                .max(180),
              phone: z
                .string()
                .min(1, tk("contacto.val.phone", "Teléfono no válido"))
                .refine((v) => isValidPhoneNumber(v), {
                  message: tk("contacto.val.phone", "Teléfono no válido"),
                }),
              reason: z
                .string()
                .trim()
                .min(20, tk("contacto.0b2eeb", "Describa con un poco más de detalle el motivo (mín. 20 caracteres)"))
                .max(4000),
            });
            const result = schema.safeParse(details);
            if (!result.success) {
              return toast.error(result.error.issues[0].message);
            }
            if (!accepted) return toast.error(tk("contacto.0468b3", "Acepte la política de privacidad"));
            setStep("choose");
          }}
          className="rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12 space-y-6 max-w-2xl mx-auto"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-3 text-[10px] tracking-[0.3em] uppercase text-gold/80">
            <span>
              {date.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long" })} · {slot.start}
            </span>
            <button type="button" onClick={() => setStep("slot")} className="text-gold/70 hover:text-gold">
              ← {tk("contacto.34c45c", "Cambiar hora")}
            </button>
          </div>
          <div className="pt-4">
            <label className="text-[10px] tracking-[0.3em] uppercase text-gold/80">
              {tk("contacto.c3a7af", "Nombre y apellidos")} *
            </label>
            <input
              type="text"
              required
              autoComplete="name"
              placeholder={tk("contacto.placeholder.name", "Ej: María García López")}
              value={details.fullName}
              onChange={(e) => setDetails({ ...details, fullName: e.target.value })}
              className="mt-2 w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Email *</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={details.email}
              onChange={(e) => setDetails({ ...details, email: e.target.value })}
              className="mt-2 w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-gold/80">
              {tk("contacto.624704", "Teléfono móvil")} *
            </label>
            <PhoneInput
              international
              defaultCountry="ES"
              value={details.phone}
              onChange={(v) => setDetails({ ...details, phone: v ?? "" })}
              className="mt-2 phone-input-gold"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-gold/80">
              {tk("contacto.253d92", "Motivo de la consulta")} *
            </label>
            <textarea
              required
              rows={6}
              placeholder={tk("contacto.cd58a9", "Cuanto más detalle aporte, mejor podremos prepararnos para su cita.")}
              value={details.reason}
              onChange={(e) => setDetails({ ...details, reason: e.target.value })}
              className="mt-2 w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2 resize-none"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              {details.reason.length} {tk("contacto.1765ca", "caracteres · mín. 20")}
            </div>
          </div>
          <label className="flex items-start gap-3 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-1 accent-[var(--gold)]"
            />
            <span>{tk("contacto.marketing", "I'd like to receive news, articles and commercial communications from PULSO33.")}</span>
          </label>
          <label className="flex items-start gap-3 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 accent-[var(--gold)]"
            />
            <span>
              {tk("contacto.6fad57.pre", "He leído y acepto la ")}
              <a
                href="/privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold underline hover:text-[oklch(0.88_0.08_85)]"
              >
                {tk("contacto.6fad57.link", "política de privacidad")}
              </a>
              .
            </span>
          </label>
          <button
            type="submit"
            className="w-full rounded-full bg-gold text-primary-foreground py-4 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
          >
            {tk("contacto.choose.continue", "Continuar")}
          </button>
        </form>
      )}

      {step === "choose" && date && slot && (
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="flex items-center justify-between flex-wrap gap-3 text-[10px] tracking-[0.3em] uppercase text-gold/80">
            <span>
              {date.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long" })} · {slot.start}
            </span>
            <button onClick={() => setStep("details")} className="text-gold/70 hover:text-gold">
              ← {tk("contacto.choose.back", "Volver a datos")}
            </button>
          </div>

          <div>
            <h3 className="font-serif text-2xl md:text-3xl text-center mb-2">
              {tk("contacto.choose.title", "Elige la modalidad")}
            </h3>
            <p className="text-xs text-muted-foreground text-center">
              {tk("contacto.choose.subtitle", "Selecciona cómo prefieres tener tu consulta.")}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {productOptions.map((p) => {
              const online = isOnlineProduct(p);
              const Icon = online ? Video : MapPin;
              const isSelected = !existingClient && selectedProduct?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setSelectedProduct(p);
                    setExistingClient(false);
                  }}
                  className={`text-left rounded-2xl border p-6 transition-colors ${
                    isSelected
                      ? "border-gold bg-gold/10"
                      : "border-gold/20 bg-card/30 hover:border-gold/60"
                  }`}
                >
                  <div className="flex items-center gap-3 text-gold mb-4">
                    <Icon size={20} strokeWidth={1.3} />
                    <span className="text-[10px] tracking-[0.3em] uppercase">
                      {online
                        ? tk("contacto.choose.online", "Online")
                        : tk("contacto.choose.inperson", "Presencial")}
                    </span>
                  </div>
                  <div className="font-serif text-xl mb-2">
                    {tk(`products.${p.slug}.name`, p.name)}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {tk(`products.${p.slug}.description`, p.description)}
                  </p>
                  <div className="text-gold font-medium">
                    {formatPrice(p.price_cents, p.currency, lang)}
                  </div>
                </button>
              );
            })}
          </div>


          <button
            type="button"
            onClick={() => {
              setExistingClient(true);
              setSelectedProduct(null);
            }}
            className={`w-full text-left rounded-2xl border p-6 transition-colors ${
              existingClient
                ? "border-gold bg-gold/10"
                : "border-gold/15 bg-card/20 hover:border-gold/60"
            }`}
          >
            <div className="flex items-center gap-3 text-gold mb-4">
              <CheckCircle2 size={20} strokeWidth={1.3} />
              <span className="text-[10px] tracking-[0.3em] uppercase">
                {tk("contacto.choose.existing.tag", "Cliente actual")}
              </span>
            </div>
            <div className="font-serif text-xl mb-2">
              {tk("contacto.choose.existing.title", "Soy cliente con un caso en curso")}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {tk(
                "contacto.choose.existing.desc",
                "Confirmaremos la cita sin coste. Te llamaremos al móvil que has indicado para validarla.",
              )}
            </p>
            <div className="text-gold font-medium">
              {formatPrice(0, product.currency, lang)}
            </div>
          </button>
          {existingClient && (
            <div className="rounded-2xl border border-gold/15 bg-card/20 p-6">
              <label className="text-[10px] tracking-[0.3em] uppercase text-gold/80">
                {tk("contacto.624704", "Teléfono móvil")} *
              </label>
              <PhoneInput
                international
                defaultCountry="ES"
                value={details.phone}
                onChange={(v) => setDetails({ ...details, phone: v ?? "" })}
                className="mt-2 phone-input-gold"
              />
            </div>
          )}

          <button
            type="button"
            disabled={
              createMutation.isPending ||
              (!existingClient && !selectedProduct) ||
              (existingClient && !isValidPhoneNumber(details.phone || ""))
            }
            onClick={() => createMutation.mutate()}
            className="w-full rounded-full bg-gold text-primary-foreground py-4 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors disabled:opacity-60"
          >
            {createMutation.isPending
              ? tk("contacto.603c0c", "Preparando…")
              : existingClient
                ? tk("contacto.choose.confirm-free", "Confirmar reserva sin coste")
                : selectedProduct
                  ? `${tk("contacto.4da14a", "Continuar al pago")} · ${formatPrice(selectedProduct.price_cents, selectedProduct.currency, lang)}`
                  : tk("contacto.choose.pickone", "Elige una modalidad")}
          </button>
        </div>
      )}

      {step === "pay" && clientSecret && (
        <div className="max-w-2xl mx-auto">
          <StripeCheckoutPanel clientSecret={clientSecret} />
        </div>
      )}

      {step === "confirmed" && date && slot && (
        <div className="max-w-2xl mx-auto rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12 text-center">
          <CheckCircle2 size={56} className="text-gold mx-auto" strokeWidth={1.2} />
          <h2 className="font-serif text-3xl md:text-4xl mt-6">
            {tk("reserva.confirmed.07da12", "Reserva")}{" "}
            <em className="not-italic gold-gradient">{tk("reserva.confirmed.8b0d05", "confirmada")}</em>
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            {tk("reserva.confirmed.138f40", "Te esperamos el")}{" "}
            <strong className="text-foreground">
              {date.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long" })}
            </strong>{" "}
            {tk("reserva.confirmed.117315", "a las")}{" "}
            <strong className="text-foreground">{slot.start}</strong>.{" "}
            {existingClient
              ? tk(
                  "reserva.confirmed.existing",
                  "Te llamaremos al móvil indicado para confirmar los detalles.",
                )
              : (
                <>
                  {tk("reserva.confirmed.ba865d", "Te hemos enviado un email a")}{" "}
                  <strong className="text-foreground">{details.email}</strong>{" "}
                  {tk("reserva.confirmed.c2e26d", "con todos los detalles.")}
                </>
              )}
          </p>
        </div>
      )}
    </section>
  );
}
